import { useQuery } from "@tanstack/react-query";
import { isNil } from "lodash-es";
import type { PerformerSeoBottomContentModel } from "src/http/api-types";
import { useCreateQueryFn } from "src/shared/hooks/useCreateQueryFn";
import { createQueryKey } from "src/shared/utils/query-helpers";
import { QueryKeyPrefix } from "src/shared/utils/query/query-key-prefixes";
import { usePerformerPageModelData } from "./usePerformerPageModelData";

/**
 * Fetches the SEO bottom content for the current performer (table rows + bio flags + CMS body + FAQ flag).
 * Fires only when the caller signals accordion proximity AND the server's deferred
 * signal (all three row fields null) is present — bots receive content via SSR and
 * this hook stays idle.
 */
export const usePerformerSeoBottomContent = (enabled = true) => {
  const model = usePerformerPageModelData();
  const createQueryFn = useCreateQueryFn();

  return useQuery<PerformerSeoBottomContentModel>({
    queryKey: createQueryKey(QueryKeyPrefix.PERFORMER_SEO_BOTTOM_CONTENT, {
      categoryId: model.categorySummary.categoryId,
    }),
    queryFn: createQueryFn({ method: "GetPerformerSeoTableRows", params: [] }),
    staleTime: Infinity,
    enabled:
      enabled &&
      isNil(model.performerEventTableRows) &&
      isNil(model.performerSportsScheduleTableRows) &&
      isNil(model.leagueTeamsTableRows),
  });
};
